import { ReactElement, ReactNode, useMemo, useState } from 'react';
import { Auth, IdentityProvider } from '../../../store';
import FormControl from '@mui/material/FormControl';
import PasswordRequirements from '../../../components/Common/PasswordRequirements';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { CabAvatar, CabButton, CabTextInput, CabImageUpload } from '@CabComponents';
import { Alert, Box, FormHelperText, FormLabel, Skeleton, styled, useTheme } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import colors from '../../../colors';
import CabSpinner from '@CabComponents/CabSpinner';
import { emailRegex } from '../../../constants';
import { setAuthErrorToSessionStorage } from '../../../utils/authUtils';
import { getIconSrc } from '../../../utils/renderUtils';

interface Props {
  auth: Auth,
  errors: string[];
  onValidatePassword: (currentPassword: string, newPassword: string, newPassword2: string) => boolean;
  onChangePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  onSaveProfile: (
    profile: { firstName: string; lastName: string },
    profilePic: { file: File, fileName: string },
  ) => Promise<void>;
  logout: (global?: boolean) => void;
  onChangeEmail: (newEmail: string) => Promise<boolean>
}

interface ProfileFormInput {
  firstName: string;
  lastName: string;
  profilePic: { file: File, fileName: string },
}

const messageDuration = 3000;

export const Account = ({
  auth, errors, onValidatePassword,
  onChangePassword, onSaveProfile, logout,
  onChangeEmail
}: Props): ReactElement => {

  const renderErrors = () => {
    const errorText = errors.join('\n\n');

    if (!errorText) {
      return null;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          width: '100%',
          marginTop: 2
        }}>
        <Slide direction="right" in={errors.length > 0}>
          <Box
            sx={{
              padding: 1,
              marginBottom: 1,
              minHeight: '30px'
            }}>
            <Typography
              variant="body1"
              sx={{
                whiteSpace: 'pre-wrap',
                display: "block",
                color: colors.redError
              }}>
              {errorText}
            </Typography>
          </Box>
        </Slide>
      </Box>
    );
  };

  return (
    <Box>
      <Profile auth={auth} onSaveProfile={onSaveProfile} />
      <ChangePassword
        auth={auth}
        onValidatePassword={onValidatePassword}
        onChangePassword={onChangePassword}
        logout={logout}
        renderErrors={renderErrors}

      />
      <ChangeEmail auth={auth} onChangeEmail={onChangeEmail} />
      <CookieDataConsent />
    </Box>
  );
};

export default Account;

export type ChangePasswordProps = {
  auth: Auth
  onValidatePassword: Props["onValidatePassword"]
  onChangePassword: Props["onChangePassword"],
  renderErrors: () => ReactNode
  logout: (global?: boolean) => void
};

const ChangePassword = ({
  auth,
  renderErrors,
  onValidatePassword,
  onChangePassword,
  logout
}: ChangePasswordProps) => {
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPassword2, setNewPassword2] = useState<string>('');
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [confirmMessage, setConfirmMessage] = useState('');

  const handleValidPassword = (valid: boolean) => {
    setButtonDisabled(!valid);
  };

  const handleClickChangePassword = async () => {
    const passwordIsValid = onValidatePassword(currentPassword, newPassword, newPassword2);
    if (passwordIsValid) {
      setButtonDisabled(true);
      try {
        const success = await onChangePassword(currentPassword, newPassword);
        if (success) {
          setConfirmMessage('Your password has been changed!');
          setCurrentPassword('');
          setNewPassword('');
          setNewPassword2('');
          setTimeout(() => {
            setAuthErrorToSessionStorage([
              "Your password was changed successfully - please sign in with your new password."
            ]);
            logout(true);
          }, messageDuration);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setButtonDisabled(false);
      }
    }
  };

  return (
    <>
      {auth.user?.identity_provider === IdentityProvider.COGNITO ?
        <AccountSection>
          <AccountSectionContent>
            <Card variant="outlined" sx={{width: '100%'}}>
              <CardContent>
                <Typography variant="h5" sx={{
                  marginBottom: 1.5
                }}>Change Password</Typography>
                <FormControl fullWidth={true}>
                  <FormLabel>Current Password</FormLabel>
                  <form>
                    {/* TODO: make this field stop autofilling! */}
                    <input type="hidden" value="prayer" />
                    <CabTextInput
                      id="currentPassword"
                      placeholder="Current Password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      inputProps={{
                        required: true,
                        autoComplete: 'please-dont',
                        form: {
                          autoComplete: 'NO'
                        },
                      }}
                      fullWidth={true}
                      autoComplete="new-password"
                    />
                  </form>
                </FormControl>
                <FormControl fullWidth={true} sx={{marginTop: 2}}>
                  <FormLabel>New Password</FormLabel>
                  <form>
                    <CabTextInput 
                      id="newPassword"
                      placeholder="New Password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      inputProps={{ required: true, autoComplete: 'please-no' }}
                      fullWidth={true}
                      sx={{marginBottom: 2}}
                    />
                    <FormLabel>Confirm New Password</FormLabel>
                    <CabTextInput 
                      id="newPassword2"
                      placeholder="New Password"
                      type="password"
                      value={newPassword2}
                      onChange={(e) => setNewPassword2(e.target.value)}
                      inputProps={{ required: true, autoComplete: 'please-no' }}
                      fullWidth={true}
                    />
                  </form>
                </FormControl>
                <PasswordRequirements password={newPassword} setValid={handleValidPassword} />
                <CabButton buttonType='primary' color='secondary' disabled={buttonDisabled}
                  sx={{marginTop: 2, width: "220px"}} onClick={handleClickChangePassword}>
                  Change Password
                </CabButton>
                {renderErrors()}
                <Snackbar
                  open={!!confirmMessage}
                  onClose={() => setConfirmMessage('')}
                  autoHideDuration={messageDuration}
                  message={confirmMessage}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                />
              </CardContent>
            </Card>
          </AccountSectionContent>
        </AccountSection>
        :
        <AccountSection>
          <AccountSectionContent>
            <Card variant="outlined" sx={{width: '100%'}}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 1
                  }}>
                  <Typography variant="h5">Change Password</Typography>
                  <Typography variant="body2" sx={{
                    display: "block"
                  }}>
                    You are logged in with {auth.user?.identity_provider === IdentityProvider.GOOGLE ?
                      "Google. Your password can be changed through your Google account."
                      :
                      "Company SSO. Your password can be changed by your company's IT team."
                    }
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </AccountSectionContent>
        </AccountSection>
      }

    </>
  );
};

export type ChangeEmailProps = {
  auth: Auth
  onChangeEmail: Props["onChangeEmail"]
};

type ChangeEmailForm = {
  email: string
  email2: string
};

const ChangeEmail = ({ auth, onChangeEmail }: ChangeEmailProps) => {

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { control, handleSubmit, getValues } = useForm<ChangeEmailForm>({
    defaultValues: {
      email: "",
      email2: ""
    }
  });

  const handleSubmitEmailChange = async (formData: ChangeEmailForm) => {
    setIsSubmitting(true);
    await onChangeEmail(formData["email"]);
    setIsSubmitting(false);
  };

  return (
    <>
      {auth.user?.identity_provider === IdentityProvider.COGNITO ?

        <>
          <form onSubmit={handleSubmit(handleSubmitEmailChange)}>
            <AccountSection>
              <AccountSectionContent>
                <Card variant="outlined" sx={{width:'100%'}}>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          width: "100%",
                          gap: 1
                        }}>
                        <Typography variant="h5">Change Email ({auth.user.email})</Typography>
                        <FormControl sx={{ flex: 1 }}>
                          <FormLabel>New Email</FormLabel>
                          <Controller
                            control={control}
                            name="email"
                            rules={{ 
                              required: true,
                              validate: (e) => {
                                const isValidEmail = emailRegex.test(e);
                                if (!isValidEmail) {
                                  return "Please enter a valid email";
                                }
                                return true;
                              }
                            }}
                            render={({ field, fieldState }) => {
                              return <><CabTextInput
                                {...field}
                              />
                              { fieldState.error?.message && <Alert 
                                color="error" sx={{marginTop: 1}}
                              >
                                {fieldState.error?.message}
                              </Alert>}
                              </>;
                            }}
                          />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                          <FormLabel>Confirm New Email</FormLabel>
                          <Controller
                            control={control}
                            name="email2"
                            rules={{ 
                              required: true,
                              validate: (e) => {
                                const isValidEmail = emailRegex.test(e);
                                if (!isValidEmail) {
                                  return "Please enter a valid email";
                                }
                                const email = getValues("email");
                                return email === e ? true : "Confirm Email doesn't match Email";
                              }
                            }}
                            render={({ field, fieldState }) => {
                              return <><CabTextInput
                                {...field}
                              />
                              { fieldState.error?.message && <Alert 
                                color="error"
                                sx={{marginTop: 1}}
                              >
                                {fieldState.error?.message}
                              </Alert>}
                              </>;
                            }}
                          />
                        </FormControl>
                        <CabButton sx={{marginTop: 2, width: "220px"}} type='submit'>
                          {isSubmitting ? <CabSpinner scale={1} color="info" /> : 'Change Email'}
                        </CabButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </AccountSectionContent>
            </AccountSection>
            
          </form>
        </>
        :
        <AccountSection>
          <AccountSectionContent>
            <Card variant="outlined" sx={{width: '100%'}}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 1
                  }}>
                  <Typography variant="h5">Change Email</Typography>
                  <Typography variant="body2" sx={{
                    display: "block"
                  }}>
                    Need to change your e-mail address? Send an email to&nbsp;
                    <a href="mailto:help@joincabinet.com">help@joincabinet.com</a> and someone from our
                    team will help you with that.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </AccountSectionContent>
        </AccountSection>
      }
    </>
  );
};

export type ProfileProps = {
  auth: Auth
  onSaveProfile: (
    profile: { firstName: string; lastName: string },
    profilePic: { file: File, fileName: string },
  ) => Promise<void>;
};

const Profile = ({
  auth, onSaveProfile
}: ProfileProps) => {
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null);

  const theme = useTheme();

  const defaultValues = useMemo(() => ({
    firstName: auth.user?.first_name || '',
    lastName: auth.user?.last_name || '',
  }), [auth.user?.first_name, auth.user?.last_name]);

  const fullName = `${auth.user ? auth.user.first_name : ''} ${auth.user ? auth.user.last_name : ''}`;

  const {
    control, reset, setValue, handleSubmit: handleFormSubmit, formState: { isSubmitting }
  } = useForm<ProfileFormInput>({ defaultValues });

  const handleCancelProfile = () => {
    setShowProfileForm(false);
    reset(defaultValues);
  };


  const handleSubmitProfile = async ({ firstName, lastName, profilePic }: ProfileFormInput) => {
    setShowProfileForm(false);
    await onSaveProfile({ firstName, lastName }, profilePic);
  };



  return (
    <AccountSection>
      <AccountSectionContent>
        <Card variant="outlined" sx={{width: '100%'}}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start'
              }}>
              {auth.user?.profile ? (
                showProfileForm ? (
                  <form onSubmit={handleFormSubmit(handleSubmitProfile)}>
                    <CabImageUpload
                      currentImage={profilePicUrl || getIconSrc(auth?.user?.profile.pic_url)}
                      avatarName={`${auth.user?.first_name} ${auth.user?.last_name}`}
                      displayMode="avatar"
                      avatarSize="large"
                      avatarColor={theme.palette.primary.main}
                      onImageSelect={(file) => {
                        setProfilePicUrl(URL.createObjectURL(file));
                        setValue('profilePic', { file, fileName: file.name });
                      }}

                      dropzoneSx={{
                        flexDirection: 'row',
                        gap: 2,
                        padding: 2,
                        marginBottom: 1,
                        border: `1px solid ${colors.black200}`,
                        borderRadius: 1
                      }}
                      previewSx={{ height: '80px', width: '80px' }}
                      dragDropText="Drag and drop file or"
                      clickText="click here"
                      fileTypeText="JPG, GIF or PNG. Max size of"
                      maxSizeText="2.5Mb"
                    />

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: 1
                      }}>
                      <FormControl sx={{ flex: 1 }}>
                        <FormLabel>First Name</FormLabel>
                        <Controller name="firstName" control={control} rules={{ required: true }}
                          render={({ field: { ref, ...field }, fieldState: { error } }) => (
                            <>
                              <CabTextInput {...field} />
                              <FormHelperText error>{error && 'First name is required'}</FormHelperText>
                            </>
                          )}
                        />
                      </FormControl>

                      <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Last Name</FormLabel>
                        <Controller name="lastName" control={control} rules={{ required: true }}
                          render={({ field: { ref, ...field }, fieldState: { error } }) => (
                            <>
                              <CabTextInput {...field} />
                              <FormHelperText error>{error && 'Last name is required'}</FormHelperText>
                            </>
                          )}
                        />
                      </FormControl>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        width: "100%"
                      }}>
                      <CabButton
                        buttonType="secondary"
                        onClick={handleCancelProfile}
                        sx={{marginTop: 2}}
                      >
                        Cancel
                      </CabButton>
                      <CabButton sx={{marginTop: 2}} type="submit">
                        Save Profile
                      </CabButton>
                    </Box>
                  </form>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start"
                    }}>
                    <CabAvatar
                      name={`${auth.user?.first_name} ${auth.user?.last_name}`}
                      src={getIconSrc(auth?.user?.profile.pic_url)}
                      color={theme.palette.primary.main}
                      size="large"
                      sx={{ marginRight: 2, height:'80px', width: '80px' }}
                    />
                    <Box sx={{
                      display: "block"
                    }}>
                      <Typography variant="h1" component="h3" sx={{ marginTop: 1 }}>{fullName}</Typography>
                      {!showProfileForm && (
                        <CabButton sx={{marginTop: 1}} onClick={() => setShowProfileForm(true)}>
                          {isSubmitting ? <CabSpinner scale={1} color="info" /> : 'Edit Name & Picture'}
                        </CabButton>
                      )}
                    </Box>
                  </Box>
                )
              ) : (
                <>
                  <Skeleton width={200} height={40} sx={{ marginTop: 1 }} />
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      </AccountSectionContent>
    </AccountSection>
  );
};

const CookieDataConsent = () => {
  const handleClickChangePrefs = () => {
    window.revisitCkyConsent();
  };

  return (
    <AccountSection>
      <AccountSectionContent>
        <Card variant="outlined" sx={{width: '100%'}}>
          <CardContent>
            <Typography variant="h5" sx={{
              marginBottom: 1.5
            }}>Cookie Preferences</Typography>
            <CabButton buttonType="primary" onClick={handleClickChangePrefs} sx={{width: "220px"}}>
              Change My Preferences
            </CabButton>
          </CardContent>
        </Card>
      </AccountSectionContent>
    </AccountSection>
  );
};

const AccountSection = styled(Box, {label: "AccountSection"})({
  display: "flex",
  width: "100%",
  marginBottom: 16,
});

const AccountSectionContent = styled(Box, {label: "AccountSectionContent"})({
  width: "100%",
  maxWidth: 900,
});
